import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, ListTodo, Plus, Search, Target, Trash2, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import {
  MONTHS_DE, APPOINTMENT_COLORS, COLOR_KEYS,
  DEFAULT_MULTIDAY_COLOR, DEFAULT_SINGLEDAY_COLOR,
  getAppointmentColor
} from "@/utils/calendarConstants";
import { getDaysInMonth, getLocalDateStr } from "@/utils/calendarUtils";
import { userStorage } from '../../utils/userStorage';
const API = process.env.REACT_APP_BACKEND_URL;

export default function YearView({ year, onMonthClick, yearPlanning, onYearPlanningChange, monthAppointments, onMonthAppointmentsChange, onDeleteFromAll, viewMode = 'calendar', todoCalendarData, onTodoCalendarHandled, yearGoals, onYearGoalChange, openTodos, appointments = [], onEditTodo, todoEditRefreshKey = 0, onToggleDoneByTaskId }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [transferMonthIndex, setTransferMonthIndex] = useState(null);
  const [selectedForTransfer, setSelectedForTransfer] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [newItem, setNewItem] = useState({ title: '', from: '', to: '', note: '', priority: 5, color: '', contactRef: null });
  const [editingItem, setEditingItem] = useState(null);
  const [editingYearGoal, setEditingYearGoal] = useState(false);
  const [yearGoalText, setYearGoalText] = useState('');

  // Wizard-Daten für Jahresanzeige
  const WIZARD_KEY = "burningteam_wizard_goals";
  const [wizardData, setWizardData] = useState(() => {
    try { return JSON.parse(userStorage.getItem(WIZARD_KEY)); } catch { return null; }
  });
  useEffect(() => {
    const refresh = () => {
      try { setWizardData(JSON.parse(userStorage.getItem(WIZARD_KEY))); } catch { setWizardData(null); }
    };
    window.addEventListener("burningteam-wizard-sync", refresh);
    return () => window.removeEventListener("burningteam-wizard-sync", refresh);
  }, []);

  // Kontakt-Verknüpfung für das Add/Edit-Modal
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const contactSearchRef = useRef(null);
  const [showYearTodosModal, setShowYearTodosModal] = useState(false);
  const [yearTodosTab, setYearTodosTab] = useState('monate'); // 'monate' or 'backlog'
  const [showMonthTodosForMonth, setShowMonthTodosForMonth] = useState(null); // monthIndex or null
  const [newMonthTodoText, setNewMonthTodoText] = useState('');
  const [selectedTodoMonth, setSelectedTodoMonth] = useState(null);
  const [todosRefresh, setTodosRefresh] = useState(0); // For forcing re-render
  const [pendingTransferTodo, setPendingTransferTodo] = useState(null); // For transfer-complete event
  const monthScrollRefs = useRef({});
  const hasScrolled = useRef(false);
  
  // Get month todos from localStorage
  const getMonthTodosForMonth = (monthIndex) => {
    try {
      const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
      const stored = JSON.parse(userStorage.getItem('zielplanung_monthly_todos') || '{}');
      return stored[monthKey] || [];
    } catch (e) {
      return [];
    }
  };
  
  // Add todo to a specific month
  const addMonthTodo = (monthIndex) => {
    if (!newMonthTodoText.trim()) return;
    // Block adding todos for past months
    if (isMonthInPast(monthIndex)) return;
    
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const stored = JSON.parse(userStorage.getItem('zielplanung_monthly_todos') || '{}');
    const todos = stored[monthKey] || [];
    
    const newTodo = {
      id: Date.now(),
      text: newMonthTodoText.trim(),
      done: false,
      createdAt: new Date().toISOString()
    };
    
    stored[monthKey] = [...todos, newTodo];
    userStorage.setItem('zielplanung_monthly_todos', JSON.stringify(stored));
    setNewMonthTodoText('');
    setTodosRefresh(prev => prev + 1); // Force re-render
  };
  
  // Toggle todo done status
  const toggleMonthTodo = (monthIndex, todoId) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const stored = JSON.parse(userStorage.getItem('zielplanung_monthly_todos') || '{}');
    const todos = stored[monthKey] || [];
    
    stored[monthKey] = todos.map(t => t.id === todoId ? { ...t, done: !t.done } : t);
    userStorage.setItem('zielplanung_monthly_todos', JSON.stringify(stored));
    setTodosRefresh(prev => prev + 1); // Force re-render
  };
  
  // Delete todo
  const deleteMonthTodo = (monthIndex, todoId) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const stored = JSON.parse(userStorage.getItem('zielplanung_monthly_todos') || '{}');
    const todos = stored[monthKey] || [];
    
    stored[monthKey] = todos.filter(t => t.id !== todoId);
    userStorage.setItem('zielplanung_monthly_todos', JSON.stringify(stored));
    setTodosRefresh(prev => prev + 1); // Force re-render
  };

  // Get current color for display
  const isMultiDay = newItem.from && newItem.to && newItem.from !== newItem.to;
  const currentColorKey = newItem.color || (isMultiDay ? DEFAULT_MULTIDAY_COLOR : DEFAULT_SINGLEDAY_COLOR);
  const currentColorData = APPOINTMENT_COLORS[currentColorKey];

  const today = new Date();

  // Clear pending transfer when modal closes (cancel case)
  useEffect(() => {
    if (!showAddModal) setPendingTransferTodo(null);
  }, [showAddModal]);

  // Kontakt-Suche: Live-Search useEffect
  useEffect(() => {
    if (!showAddModal || !contactSearch.trim() || newItem.contactRef) {
      if (!newItem.contactRef) setContactResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/teampartner?typ=`);
        const q = contactSearch.toLowerCase();
        setContactResults((res.data || []).filter(c => c.name?.toLowerCase().includes(q)).slice(0, 6));
      } catch { setContactResults([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [contactSearch, showAddModal, newItem.contactRef]);

  // Click-outside für Kontakt-Suche
  useEffect(() => {
    const handleClick = (e) => {
      if (contactSearchRef.current && !contactSearchRef.current.contains(e.target)) setContactSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Handle todo-to-calendar transfer
  useEffect(() => {
    if (todoCalendarData && todoCalendarData.type === 'year') {
      // Set selected month from todo dueDate or current month
      const dateStr = todoCalendarData.dueDate || getLocalDateStr(today);
      const monthIdx = parseInt(dateStr.split('-')[1]) - 1;
      setSelectedMonth(monthIdx);
      setNewItem({
        title: todoCalendarData.title || '',
        from: dateStr,
        to: dateStr,
        note: todoCalendarData.note || '',
        priority: todoCalendarData.priority || 5,
        color: '',
        contactRef: todoCalendarData.contactRef || null,
      });
      // Sync contactSearch display state
      setContactSearch(todoCalendarData.contactRef?.name || '');
      // Store pending transfer info so we can delete todo AFTER successful save
      if (todoCalendarData.todoId) {
        setPendingTransferTodo({ id: todoCalendarData.todoId, fromTab: todoCalendarData.fromTab });
      }
      setShowAddModal(true);
      // Clear the data
      if (onTodoCalendarHandled) {
        onTodoCalendarHandled();
      }
    }
  }, [todoCalendarData, onTodoCalendarHandled]);

  // Get days in a specific month
  const getDaysInMonth = (monthIndex) => {
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  // Get items that START on a specific day (not spanning through)
  // Read from localStorage for most up-to-date data (esp. transferredToWeek flag)
  const getItemsStartingOnDay = (monthIndex, day) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    let items = yearPlanning[monthKey] || [];
    // Also read from localStorage for most up-to-date data
    try {
      const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
      const storedItems = stored?.[monthKey] || [];
      if (storedItems.length > 0) {
        items = storedItems;
      }
    } catch (e) {}
    const dayStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const yearItems = items.filter(item => item.from === dayStr);
    // Also include habit reminder entries from appointments (short individual day entries)
    const habitItems = (appointments || [])
      .filter(apt => apt.isHabitReminder && apt.date === dayStr)
      .map(apt => ({
        id: apt.id,
        title: apt.title,
        from: apt.date,
        to: apt.date,
        note: apt.note,
        priority: apt.priority || 10,
        color: 'blue',
        isHabitReminder: true,
        habitId: apt.habitId,
        startTime: apt.startTime,
        endTime: apt.endTime,
        completed: apt.completed
      }));
    // Also include regular week-view appointments (single-day, non-habit, non-multi-day)
    const regularItems = (appointments || [])
      .filter(apt => !apt.isHabitReminder && !apt.isMultiDay && apt.date === dayStr)
      .map(apt => ({
        id: apt.id,
        title: apt.title,
        from: apt.date,
        to: apt.date,
        note: apt.note,
        priority: apt.priority || 5,
        color: apt.color || 'blue',
        startTime: apt.startTime,
        endTime: apt.endTime,
        done: apt.done,
        contactRef: apt.contactRef,
        isWeekAppointment: true,
      }));
    return [...yearItems, ...habitItems, ...regularItems];
  };

  // Get all multi-day items for a month (for consistent color assignment)
  const getMultiDayItems = (monthIndex) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const items = yearPlanning[monthKey] || [];
    return items.filter(item => item.from !== item.to).sort((a, b) => a.id - b.id);
  };

  // Get color index for a specific item (consistent across all days)
  const getItemColorIndex = (monthIndex, itemId) => {
    const multiDayItems = getMultiDayItems(monthIndex);
    const idx = multiDayItems.findIndex(item => item.id === itemId);
    return idx >= 0 ? idx : 0;
  };

  // Get all spans that cover this day (but don't start on it) - for showing multiple indicators
  const getSpansForDay = (monthIndex, day) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const items = yearPlanning[monthKey] || [];
    const dayStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return items.filter(item => {
      const toDate = item.to || item.from;
      return item.from !== dayStr && dayStr > item.from && dayStr <= toDate;
    }).sort((a, b) => a.id - b.id);
  };

  // Colors for span indicators and item backgrounds
  const spanColors = ['text-blue-500', 'text-purple-500', 'text-orange-500', 'text-pink-500', 'text-cyan-500'];
  const borderColors = ['border-blue-500', 'border-purple-500', 'border-orange-500', 'border-pink-500', 'border-cyan-500'];
  const bgColors = ['bg-blue-100', 'bg-purple-100', 'bg-orange-100', 'bg-pink-100', 'bg-cyan-100'];
  const textColors = ['text-blue-700', 'text-purple-700', 'text-orange-700', 'text-pink-700', 'text-cyan-700'];

  // Priority border colors for left indicator
  const getPriorityBorderColor = (priority) => {
    if (priority >= 8) return 'border-red-500';
    if (priority >= 5) return 'border-amber-500';
    return 'border-green-500';
  };

  // Get all items for a month
  const getMonthItems = (monthIndex) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    let items = yearPlanning[monthKey] || [];
    // Also read from localStorage for most up-to-date data (esp. transferredToWeek flag)
    try {
      const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
      const storedItems = stored?.[monthKey] || [];
      if (storedItems.length > 0) {
        items = storedItems;
      }
    } catch (e) {}
    // Also include habit reminder entries from appointments for this month
    const habitItems = (appointments || [])
      .filter(apt => apt.isHabitReminder && apt.date && apt.date.startsWith(monthKey))
      .map(apt => ({
        id: apt.id,
        title: apt.title,
        from: apt.date,
        to: apt.date,
        note: apt.note,
        priority: apt.priority || 10,
        color: 'blue',
        isHabitReminder: true,
        habitId: apt.habitId,
        startTime: apt.startTime,
        endTime: apt.endTime,
        completed: apt.completed
      }));
    // Also include regular week-view appointments (single-day, non-habit, non-multi-day)
    const regularItems = (appointments || [])
      .filter(apt => !apt.isHabitReminder && !apt.isMultiDay && apt.date && apt.date.startsWith(monthKey))
      .map(apt => ({
        id: apt.id,
        title: apt.title,
        from: apt.date,
        to: apt.date,
        note: apt.note,
        priority: apt.priority || 5,
        color: apt.color || 'blue',
        startTime: apt.startTime,
        endTime: apt.endTime,
        done: apt.done,
        contactRef: apt.contactRef,
        isWeekAppointment: true,
      }));
    return [...items, ...habitItems, ...regularItems];
  };

  // Scroll to first entry day within each month container on initial mount
  useEffect(() => {
    if (hasScrolled.current) return;
    hasScrolled.current = true;
    
    setTimeout(() => {
      MONTHS_DE.forEach((_, monthIndex) => {
        const ref = monthScrollRefs.current[monthIndex];
        if (ref) {
          const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
          const items = yearPlanning[monthKey] || [];
          
          if (items.length > 0) {
            // Find the earliest start day
            const days = items.map(item => parseInt(item.from.split('-')[2])).sort((a, b) => a - b);
            const firstDay = days[0] || 1;
            
            // Find the day element and scroll container to it
            const dayElement = ref.querySelector(`[data-day="${firstDay}"]`);
            if (dayElement) {
              ref.scrollTop = dayElement.offsetTop - ref.offsetTop;
            }
          }
        }
      });
    }, 100);
  }, []);

  const handleDayClick = (monthIndex, day) => {
    setSelectedMonth(monthIndex);
    setSelectedDay(day);
    setEditingItem(null);
    const monthStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const dayStr = `${monthStr}-${String(day).padStart(2, '0')}`;
    setNewItem({ title: '', from: dayStr, to: dayStr, note: '', priority: 5, color: '', contactRef: null });
    setContactSearch(''); setContactResults([]);
    setShowAddModal(true);
  };
  
  // Helper: check if a month is in the past
  const isMonthInPast = (monthIndex) => {
    const today = new Date();
    if (year < today.getFullYear()) return true;
    if (year === today.getFullYear() && monthIndex < today.getMonth()) return true;
    return false;
  };
  
  // Helper: check if a day is in the past
  const isDayInPast = (monthIndex, day) => {
    const clickedDate = new Date(year, monthIndex, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return clickedDate < today;
  };

  const handleEditItem = (monthIndex, item) => {
    setSelectedMonth(monthIndex);
    setEditingItem(item);
    setNewItem({ 
      title: item.title || item.note || '',
      from: item.from || '',
      to: item.to || '',
      note: item.note || '',
      priority: item.priority || 5,
      color: item.color || '',
      contactRef: item.contactRef || null,
    });
    setContactSearch(item.contactRef?.name || '');
    setContactResults([]);
    setShowColorPicker(false);
    setShowAddModal(true);
  };

  // Handle date change with validation
  const handleDateChange = (field, value) => {
    if (field === 'from') {
      // If new start is after current end, also update end
      if (newItem.to && value > newItem.to) {
        setNewItem({ ...newItem, from: value, to: value });
      } else {
        setNewItem({ ...newItem, from: value });
      }
    } else if (field === 'to') {
      // End date cannot be before start date
      if (value < newItem.from) {
        setNewItem({ ...newItem, to: newItem.from });
      } else {
        setNewItem({ ...newItem, to: value });
      }
    }
  };

  const handleSaveItem = () => {
    if (!newItem.title.trim()) return;
    
    const monthKey = `${year}-${String(selectedMonth + 1).padStart(2, '0')}`;
    const currentItems = yearPlanning[monthKey] || [];
    
    if (editingItem) {
      const updatedItems = currentItems.map(item => 
        item.id === editingItem.id ? { ...newItem, id: editingItem.id, transferred: item.transferred } : item
      );
      onYearPlanningChange(monthKey, updatedItems);
      
      // If the item was transferred, also update in monthAppointments (for multi-day items)
      // Single-day items are rendered directly from yearPlanning, multi-day need month sync
      if (editingItem.transferred && onMonthAppointmentsChange) {
        const currentMonthApts = monthAppointments?.[monthKey] || [];
        // Check if this item exists in month appointments (multi-day transferred items)
        const monthItem = currentMonthApts.find(apt => apt.id === editingItem.id);
        if (monthItem) {
          const updatedMonthApts = currentMonthApts.map(apt => 
            apt.id === editingItem.id ? { ...newItem, id: editingItem.id, isYearItem: true, transferred: true } : apt
          );
          onMonthAppointmentsChange(monthKey, updatedMonthApts);
        }
      }
    } else {
      const updatedItems = [...currentItems, { ...newItem, id: Date.now() }];
      onYearPlanningChange(monthKey, updatedItems);
    }

    // Fire transfer-complete event so TodosView deletes the source todo
    if (pendingTransferTodo) {
      window.dispatchEvent(new CustomEvent('burningteam-transfer-complete', { detail: pendingTransferTodo }));
      setPendingTransferTodo(null);
    }
    
    setShowAddModal(false);
    setEditingItem(null);
    setShowColorPicker(false);
    setNewItem({ title: '', from: '', to: '', note: '', priority: 5, color: '', contactRef: null });
    setContactSearch('');
    setContactResults([]);
  };

  // "Erledigt"-Toggle für Jahres-Einträge mit Kontakt-Verknüpfung
  const handleToggleYearDone = async (item) => {
    const newDone = !item.done;
    setShowAddModal(false);
    setEditingItem(null);
    setContactSearch('');

    if (onToggleDoneByTaskId && item.contactRef?.taskId) {
      // Zentraler Callback: aktualisiert Woche + Monat + Jahr + Kontakt-API
      await onToggleDoneByTaskId(item.contactRef.taskId, newDone, item.contactRef);
    } else {
      // Fallback: nur Jahr aktualisieren
      const monthKey = `${year}-${String(selectedMonth + 1).padStart(2, '0')}`;
      const currentItems = yearPlanning[monthKey] || [];
      const updatedItems = currentItems.map(i => i.id === item.id ? { ...i, done: newDone } : i);
      onYearPlanningChange(monthKey, updatedItems);
      if (item.contactRef?.id && item.contactRef?.taskId) {
        try {
          const endpoint = item.contactRef.typ === 'ich'
            ? `${API}/api/auth/me/tasks/${item.contactRef.taskId}`
            : `${API}/api/teampartner/${item.contactRef.id}/tasks/${item.contactRef.taskId}`;
          await axios.patch(endpoint, { done: newDone });
        } catch { }
      }
    }
  };

  // Execute the actual delete from the modal
  const executeDeleteFromModal = () => {
    if (!editingItem) return;
    const monthKey = `${year}-${String(selectedMonth + 1).padStart(2, '0')}`;
    if (onDeleteFromAll) {
      onDeleteFromAll(editingItem.id, monthKey);
    }
    setShowDeleteConfirm(false);
    setShowAddModal(false);
    setEditingItem(null);
  };

  const handleDeleteItem = (monthKey, itemId, e) => {
    if (e) e.stopPropagation();
    
    // Use central delete function if available
    if (onDeleteFromAll) {
      onDeleteFromAll(itemId, monthKey);
    } else {
      // Fallback: just delete from year planning
      const currentItems = yearPlanning[monthKey] || [];
      const updatedItems = currentItems.filter(item => item.id !== itemId);
      onYearPlanningChange(monthKey, updatedItems);
    }
  };

  // Open transfer modal for a month (simple selection modal for YearView)
  const handleOpenTransferModal = (monthIndex) => {
    setTransferMonthIndex(monthIndex);
    setSelectedForTransfer([]);
    setShowTransferModal(true);
  };

  // Toggle item selection for transfer
  const toggleTransferSelection = (itemId) => {
    setSelectedForTransfer(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Execute transfer - mark items as transferred
  const executeTransfer = () => {
    if (selectedForTransfer.length === 0 || transferMonthIndex === null) return;
    
    const monthKey = `${year}-${String(transferMonthIndex + 1).padStart(2, '0')}`;
    const currentItems = yearPlanning[monthKey] || [];
    
    // Mark selected items as transferred
    const updatedItems = currentItems.map(item => 
      selectedForTransfer.includes(item.id) 
        ? { ...item, transferred: true }
        : item
    );
    
    onYearPlanningChange(monthKey, updatedItems);
    setShowTransferModal(false);
    setSelectedForTransfer([]);
    setTransferMonthIndex(null);
  };

  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'bg-red-100 border-red-300 text-red-700';
    if (priority >= 5) return 'bg-amber-100 border-amber-300 text-amber-700';
    return 'bg-green-100 border-green-300 text-green-700';
  };

  return (
    <div className="space-y-4">
      {/* Kompakter Header: Jahresziel + Jahres-Aufgaben */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3" key={todosRefresh}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
          {/* Jahresziel - einzeilig, nicht editierbar */}
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Target className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm font-medium text-slate-700 shrink-0">Jahresziel:</span>
            {wizardData?.yearGoal && wizardData.year === year ? (
              <span className={`text-sm truncate flex-1 min-w-0 ${wizardData.yearGoal.done ? 'line-through text-slate-400' : 'text-slate-600'}`}>
                {wizardData.yearGoal.done && <span className="text-emerald-600 mr-1 font-bold">✓</span>}
                {wizardData.yearGoal.number} {wizardData.yearGoal.text}
              </span>
            ) : (
              <span className="text-sm text-slate-400 italic">Nicht geplant</span>
            )}
          </div>

          {/* Divider */}
          <div className="hidden sm:block w-px h-6 bg-slate-200 shrink-0"></div>
          
          <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end sm:gap-1">
          {/* Jahres-Aufgaben */}
          <div 
            onClick={() => setShowYearTodosModal(true)}
            className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-purple-50 rounded-lg px-1 py-0.5 sm:px-2 sm:py-1 transition-colors"
          >
            <ListTodo className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Aufgaben:</span>
            {(() => {
              let totalOpen = 0;
              let totalDone = 0;
              for (let m = 0; m < 12; m++) {
                const todos = getMonthTodosForMonth(m);
                totalOpen += todos.filter(t => !t.done).length;
                totalDone += todos.filter(t => t.done).length;
              }
              return (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{totalOpen} offen</span>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{totalDone} ✓</span>
                </div>
              );
            })()}
          </div>
          
          {/* Neuer Termin Button - nach rechts verschoben */}
          <button 
            onClick={() => {
              const now = new Date();
              const currentMonth = now.getMonth();
              const dayStr = `${year}-${String(currentMonth + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
              setSelectedMonth(currentMonth);
              setSelectedDay(now.getDate());
              setEditingItem(null);
              setShowColorPicker(false);
              setNewItem({ title: '', from: dayStr, to: dayStr, note: '', priority: 5, color: '', contactRef: null });
              setContactSearch(''); setContactResults([]);
              setShowAddModal(true);
            }}
            className="w-7 h-7 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded transition-colors flex items-center justify-center"
            title="Neuer Termin"
            data-testid="year-view-new-appointment-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
          </div>
        </div>
      </div>

      {/* Jahresziel Edit Modal */}
      {editingYearGoal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Jahresziel {year}
              </h3>
              <button onClick={() => setEditingYearGoal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea 
              value={yearGoalText} 
              onChange={(e) => setYearGoalText(e.target.value)} 
              placeholder="Was möchtest du dieses Jahr erreichen?" 
              className="w-full p-3 border border-slate-200 rounded-lg text-sm" 
              rows={4} 
              autoFocus 
            />
            <div className="flex gap-2 mt-4">
              <Button onClick={() => { onYearGoalChange && onYearGoalChange(year, yearGoalText); setEditingYearGoal(false); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Speichern</Button>
              <Button onClick={() => setEditingYearGoal(false)} variant="outline">Abbrechen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Jahres-Aufgaben Modal - Übersicht aller Monate */}
      {showYearTodosModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl p-4 shadow-xl max-h-[85vh] overflow-hidden flex flex-col ${showMonthTodosForMonth !== null ? 'max-w-md w-full' : 'max-w-4xl w-full'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-purple-600" />
                {showMonthTodosForMonth !== null 
                  ? `Aufgaben - ${MONTHS_DE[showMonthTodosForMonth]} ${year}`
                  : `Jahres-Aufgaben ${year}`
                }
              </h3>
              <button onClick={() => { setShowYearTodosModal(false); setShowMonthTodosForMonth(null); setYearTodosTab('monate'); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tab Toggle: Monate / Dieses Jahr (Backlog) */}
            {showMonthTodosForMonth === null && (
              <div className="flex gap-1 mb-4 p-1 bg-slate-100 rounded-lg">
                <button
                  onClick={() => setYearTodosTab('monate')}
                  className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${yearTodosTab === 'monate' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Monate
                </button>
                <button
                  onClick={() => setYearTodosTab('backlog')}
                  className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${yearTodosTab === 'backlog' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Dieses Jahr / Backlog
                  {openTodos && openTodos.filter(t => !t.done).length > 0 && (
                    <span className="ml-1.5 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                      {openTodos.filter(t => !t.done).length}
                    </span>
                  )}
                </button>
              </div>
            )}
            {showMonthTodosForMonth !== null && (() => {
              const todos = getMonthTodosForMonth(showMonthTodosForMonth);
              const openCount = todos.filter(t => !t.done).length;
              const doneCount = todos.filter(t => t.done).length;
              const isCurrentMonth = new Date().getMonth() === showMonthTodosForMonth && new Date().getFullYear() === year;
              
              return (
                <div className="flex-1 overflow-y-auto">
                  <div className={`rounded-xl border-2 p-4 ${isCurrentMonth ? 'border-emerald-400 bg-emerald-50' : 'border-purple-400 bg-purple-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold ${isCurrentMonth ? 'text-emerald-700' : 'text-purple-700'}`}>
                          {MONTHS_DE[showMonthTodosForMonth]}
                        </span>
                        {isCurrentMonth && <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">Aktuell</span>}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {openCount > 0 && <span className="bg-purple-200 text-purple-700 px-2 py-1 rounded-full">{openCount} offen</span>}
                        {doneCount > 0 && <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">✓ {doneCount}</span>}
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
                      {todos.length === 0 ? (
                        <p className="text-slate-400 text-sm text-center py-4">Keine Aufgaben für diesen Monat</p>
                      ) : (
                        todos.map(todo => (
                          <div key={todo.id} className={`p-3 rounded-lg flex items-center gap-3 ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                            <button 
                              onClick={() => toggleMonthTodo(showMonthTodosForMonth, todo.id)}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}
                            >
                              {todo.done && <Check className="w-3 h-3 text-white" />}
                            </button>
                            <button
                              onClick={() => onEditTodo && onEditTodo(todo, 'monthly', { monthKey: `${year}-${String(showMonthTodosForMonth + 1).padStart(2, '0')}` })}
                              className={`flex-1 text-left text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}
                            >
                              {todo.text}
                            </button>
                            <button onClick={() => deleteMonthTodo(showMonthTodosForMonth, todo.id)} className="text-slate-300 hover:text-red-500 p-1">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Add new todo - only for future months */}
                    {!isMonthInPast(showMonthTodosForMonth) ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={selectedTodoMonth === showMonthTodosForMonth ? newMonthTodoText : ''}
                        onChange={(e) => { setSelectedTodoMonth(showMonthTodosForMonth); setNewMonthTodoText(e.target.value); }}
                        onFocus={() => setSelectedTodoMonth(showMonthTodosForMonth)}
                        onKeyDown={(e) => e.key === 'Enter' && addMonthTodo(showMonthTodosForMonth)}
                        placeholder="+ Neue Aufgabe hinzufügen..."
                        className="flex-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                      />
                      <button onClick={() => addMonthTodo(showMonthTodosForMonth)} className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    ) : (
                    <div className="text-xs text-slate-400 text-center py-2">Vergangener Monat – nicht mehr bearbeitbar</div>
                    )}
                  </div>
                  
                  <button 
                    onClick={() => setShowMonthTodosForMonth(null)} 
                    className="w-full mt-4 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    Alle Monate anzeigen →
                  </button>
                </div>
              );
            })()}

            {/* All Months Grid */}
            {showMonthTodosForMonth === null && yearTodosTab === 'monate' && (
              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {MONTHS_DE.map((monthName, monthIndex) => {
                    const todos = getMonthTodosForMonth(monthIndex);
                    const openCount = todos.filter(t => !t.done).length;
                    const doneCount = todos.filter(t => t.done).length;
                    const isCurrentMonth = new Date().getMonth() === monthIndex && new Date().getFullYear() === year;
                    
                    return (
                      <div 
                        key={monthIndex}
                        className={`rounded-xl border-2 p-3 ${
                          isCurrentMonth 
                            ? 'border-emerald-400 bg-emerald-50' 
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${isCurrentMonth ? 'text-emerald-700' : 'text-slate-700'}`}>
                              {monthName}
                            </span>
                            {isCurrentMonth && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">Aktuell</span>}
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            {openCount > 0 && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{openCount}</span>}
                            {doneCount > 0 && <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">✓{doneCount}</span>}
                          </div>
                        </div>
                        
                        {/* Todo list preview */}
                        <div className="space-y-1 mb-2 max-h-[100px] overflow-y-auto">
                          {todos.length === 0 ? (
                            <p className="text-slate-400 text-xs text-center py-2">Keine Aufgaben</p>
                          ) : (
                            todos.slice(0, 3).map(todo => (
                              <div key={todo.id} className={`p-1.5 rounded flex items-center gap-1.5 text-xs ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); toggleMonthTodo(monthIndex, todo.id); }}
                                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}
                                >
                                  {todo.done && <Check className="w-2 h-2 text-white" />}
                                </button>
                                <span className={`truncate ${todo.done ? 'line-through' : ''}`}>{todo.text}</span>
                              </div>
                            ))
                          )}
                          {todos.length > 3 && (
                            <p className="text-xs text-purple-500 text-center">+{todos.length - 3} weitere</p>
                          )}
                        </div>
                        
                        {/* Add new todo inline - only for future months */}
                        {!isMonthInPast(monthIndex) ? (
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={selectedTodoMonth === monthIndex ? newMonthTodoText : ''}
                            onChange={(e) => { setSelectedTodoMonth(monthIndex); setNewMonthTodoText(e.target.value); }}
                            onFocus={() => setSelectedTodoMonth(monthIndex)}
                            onKeyDown={(e) => e.key === 'Enter' && addMonthTodo(monthIndex)}
                            placeholder="+ Neu..."
                            className="flex-1 p-1.5 border border-slate-200 rounded text-xs bg-white min-w-0"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <button 
                            onClick={(e) => { e.stopPropagation(); addMonthTodo(monthIndex); }} 
                            className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        ) : (
                        <div className="text-[10px] text-slate-400 text-center py-1">Vergangen</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Backlog / Dieses Jahr View */}
            {showMonthTodosForMonth === null && yearTodosTab === 'backlog' && (
              <div className="flex-1 overflow-y-auto">
                {(!openTodos || openTodos.length === 0) ? (
                  <p className="text-slate-400 text-sm text-center py-8">Keine Jahresaufgaben vorhanden</p>
                ) : (
                  <div className="space-y-2">
                    {openTodos.map(todo => (
                      <div key={todo.id} className={`p-3 rounded-xl border flex items-center gap-3 ${todo.done ? 'bg-slate-50 border-slate-200' : 'bg-purple-50 border-purple-200'}`}>
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: todo.done ? '#94a3b8' : '#9333ea' }}></div>
                        <button
                          onClick={() => onEditTodo && onEditTodo(todo, 'open', {})}
                          className={`flex-1 text-left text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}
                        >
                          {todo.text}
                        </button>
                        {todo.month !== undefined && (
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            {MONTHS_DE[todo.month]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {MONTHS_DE.map((month, monthIndex) => {
          const isCurrentMonth = new Date().getMonth() === monthIndex && new Date().getFullYear() === year;
          const daysInMonth = getDaysInMonth(monthIndex);
          const today = new Date();
          const isCurrentYear = today.getFullYear() === year;
          const currentDay = isCurrentYear && today.getMonth() === monthIndex ? today.getDate() : null;
          
          return (
            <div
              key={month}
              className={`rounded-xl overflow-hidden transition-all ${
                isCurrentMonth ? 'ring-2 ring-emerald-500' : 'border border-slate-200'
              }`}
            >
              {/* Month Header */}
              <button
                onClick={() => handleDayClick(monthIndex, 1)}
                className={`w-full p-2 text-center transition-all hover:opacity-90 ${
                  isCurrentMonth ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span className="font-semibold text-sm">{month}</span>
                <span className="ml-1 text-xs opacity-75">+ Hinzufügen</span>
              </button>
              
              {/* Days List - Scrollable (Calendar Mode) */}
              {viewMode === 'calendar' && (
                <div 
                  ref={el => monthScrollRefs.current[monthIndex] = el}
                  className="bg-white max-h-[140px] overflow-y-auto"
                >
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const dayItems = getItemsStartingOnDay(monthIndex, day);
                    const spansOnDay = getSpansForDay(monthIndex, day);
                    const isToday = currentDay === day;
                    const hasItems = dayItems.length > 0;
                    const hasSpans = spansOnDay.length > 0;
                    
                    return (
                      <div 
                        key={day}
                        data-day={day}
                        className={`border-b border-slate-100 last:border-b-0 ${isToday ? 'bg-emerald-50' : ''} ${hasSpans ? 'bg-slate-50/50' : ''}`}
                      >
                        {/* Day Row */}
                        <div 
                          className={`flex items-center px-2 py-1 cursor-pointer hover:bg-slate-50 ${isToday ? 'hover:bg-emerald-100' : ''}`}
                          onClick={() => handleDayClick(monthIndex, day)}
                        >
                          <span className={`text-xs font-medium w-5 ${isToday ? 'text-emerald-600' : 'text-slate-500'}`}>
                            {day}
                          </span>
                          {!hasItems && !hasSpans && (
                            <span className="text-[10px] text-slate-300 ml-1">—</span>
                          )}
                          {/* Multiple span indicators in different colors (consistent per item) - show even when hasItems */}
                          {hasSpans && (
                            <span className="flex items-center ml-1 gap-0">
                              {spansOnDay.map((span) => {
                                const colorIdx = getItemColorIndex(monthIndex, span.id);
                                return (
                                  <span key={span.id} className={`text-[10px] ${spanColors[colorIdx % spanColors.length]}`}>│</span>
                                );
                              })}
                            </span>
                          )}
                        </div>
                        
                        {/* Items that START on this day */}
                        {hasItems && (
                          <div className="px-2 pb-1 space-y-0.5">
                            {dayItems.map((item) => {
                              const isMultiDay = item.from !== item.to;
                              const fromDay = parseInt(item.from.split('-')[2]);
                              const toDay = item.to ? parseInt(item.to.split('-')[2]) : fromDay;
                              // Show title, fallback to note for old items
                              const displayTitle = item.title || item.note;
                              const priorityBorder = getPriorityBorderColor(item.priority || 5);
                              // Get consistent color index for multi-day items
                              const colorIndex = isMultiDay ? getItemColorIndex(monthIndex, item.id) : 0;
                              const aptColor = getAppointmentColor(item, colorIndex);
                              
                              return (
                                <div 
                                  key={item.id}
                                  className={`text-[10px] p-1 rounded-r border-l-4 ${priorityBorder} ${aptColor.light} cursor-pointer ${item.done ? 'opacity-70' : ''}`}
                                  onClick={() => handleEditItem(monthIndex, item)}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <span className={`font-medium line-clamp-2 block ${aptColor.lightText} ${item.isHabitReminder && item.completed ? 'line-through opacity-70' : ''} ${item.done && !item.isHabitReminder ? 'line-through opacity-75' : ''}`}>
                                        {item.isHabitReminder && item.completed && <span className="text-emerald-600 mr-0.5">✓</span>}
                                        {item.done && !item.isHabitReminder && <span className="mr-0.5">☑ </span>}
                                        {displayTitle}
                                      </span>
                                      {isMultiDay && (
                                        <span className={`text-[9px] opacity-75 ${aptColor.lightText}`}>{fromDay}.-{toDay}.</span>
                                      )}
                                    </div>
                                    <button 
                                      onClick={(e) => handleDeleteItem(`${year}-${String(monthIndex + 1).padStart(2, '0')}`, item.id, e)}
                                      className="text-slate-400 hover:text-red-500 p-0.5 ml-1"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                  {/* Show transferred indicator */}
                                  {item.transferred && !item.transferredToWeek && (
                                    <div className="text-[9px] text-emerald-600 font-medium mt-0.5">✓ zum Monat</div>
                                  )}
                                  {item.transferredToWeek && (
                                    <div className="text-[9px] text-blue-600 font-medium mt-0.5">✓ zur Woche</div>
                                  )}
                                  {item.done && !item.transferred && !item.transferredToWeek && (
                                    <div className="text-[9px] text-emerald-700 font-medium mt-0.5">☑ Erledigt</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Items List Only (List Mode) - No days, just entries */}
              {viewMode === 'list' && (
                <div className="bg-white max-h-[140px] overflow-y-auto p-1 space-y-1">
                  {(() => {
                    const monthItems = getMonthItems(monthIndex);
                    if (monthItems.length === 0) {
                      return <div className="p-3 text-center text-slate-400 text-xs">Keine Einträge</div>;
                    }
                    return monthItems.sort((a, b) => (a.from || '').localeCompare(b.from || '')).map((item) => {
                      const fromDay = item.from ? parseInt(item.from.split('-')[2]) : 0;
                      const toDay = item.to ? parseInt(item.to.split('-')[2]) : fromDay;
                      const isMultiDay = item.from !== item.to;
                      const displayTitle = item.title || item.note;
                      const priorityBorder = getPriorityBorderColor(item.priority || 5);
                      // Get consistent color index for multi-day items
                      const colorIndex = isMultiDay ? getItemColorIndex(monthIndex, item.id) : 0;
                      const aptColor = getAppointmentColor(item, colorIndex);
                      
                      return (
                        <div 
                          key={item.id}
                          className={`px-2 py-1.5 cursor-pointer hover:bg-slate-50 border-l-4 ${aptColor.light} rounded-r shadow-sm ${priorityBorder}`}
                          onClick={() => handleEditItem(monthIndex, item)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <span className={`text-xs font-medium line-clamp-1 ${aptColor.lightText} ${item.isHabitReminder && item.completed ? 'line-through opacity-70' : ''}`}>
                                {item.isHabitReminder && item.completed && <span className="text-emerald-600 mr-0.5">✓</span>}
                                {displayTitle}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <span className="text-[10px] text-slate-400 whitespace-nowrap">
                                {fromDay}.{isMultiDay ? `-${toDay}.` : ''}
                              </span>
                              <button 
                                onClick={(e) => handleDeleteItem(`${year}-${String(monthIndex + 1).padStart(2, '0')}`, item.id, e)}
                                className="text-slate-400 hover:text-red-500 p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          {item.transferred && !item.transferredToWeek && (
                            <div className="text-[9px] text-emerald-600 font-medium">✓ zum Monat</div>
                          )}
                          {item.transferredToWeek && (
                            <div className="text-[9px] text-blue-600 font-medium">✓ zur Woche</div>
                          )}
                          {item.contactRef?.name && (
                            <div className="text-[9px] text-emerald-600 font-medium flex items-center gap-0.5">
                              <User className="w-2.5 h-2.5" />{item.contactRef.name}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
              
              {/* Footer Buttons - Styled */}
              <div className="p-2 space-y-1.5 border-t border-slate-100">
                <button
                  onClick={() => handleOpenTransferModal(monthIndex)}
                  className="w-full py-2 px-3 text-sm font-semibold text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                >
                  <span className="text-base">→</span> Übertragen
                </button>
                <button
                  onClick={() => { setShowMonthTodosForMonth(monthIndex); setShowYearTodosModal(true); }}
                  className="w-full py-2 px-3 text-sm font-semibold text-white bg-purple-500 hover:bg-purple-600 rounded-lg transition-colors flex items-center justify-center gap-1"
                >
                  <ListTodo className="w-4 h-4" /> Monats-Aufgaben
                </button>
                <button
                  onClick={() => onMonthClick(monthIndex)}
                  className="w-full py-2 px-3 text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors"
                >
                  <span className="text-base">▶</span> zum Monat
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingItem ? 'Eintrag bearbeiten' : `Eintrag für ${MONTHS_DE[selectedMonth]} ${year}`}
              </h3>
              <button onClick={() => { setShowAddModal(false); setEditingItem(null); setShowColorPicker(false); setShowDeleteConfirm(false); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {showDeleteConfirm ? (
              <div className="text-center py-4">
                <p className="text-slate-700 mb-2">Eintrag wirklich löschen?</p>
                <p className="text-sm text-slate-500 mb-4">Der Eintrag wird aus allen Ansichten (Jahr, Monat, Woche) entfernt.</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={executeDeleteFromModal} className="bg-red-600 hover:bg-red-700 text-white">
                    <Trash2 className="w-4 h-4 mr-2" /> Löschen
                  </Button>
                  <Button onClick={() => setShowDeleteConfirm(false)} variant="outline">
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
            <div className="space-y-4">
              {/* Title with color picker */}
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <label className="text-sm font-medium text-slate-700">Titel</label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Termin Titel"
                    className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"
                    autoFocus
                  />
                </div>
                {/* Color picker button */}
                <div className="relative pt-5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowColorPicker(!showColorPicker);
                    }}
                    className={`w-10 h-10 rounded-full ${currentColorData.bg} shadow-md active:scale-95 transition-all ring-2 ring-white`}
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                    title="Farbe wählen"
                  />
                  {showColorPicker && (
                    <div 
                      className="absolute bg-white rounded-xl shadow-xl border border-slate-200 p-3 z-[9999]"
                      style={{ right: '-10px', top: '52px', minWidth: '160px' }}
                    >
                      <p className="text-xs text-slate-500 mb-2 text-center font-medium">Farbe wählen</p>
                      <div className="grid grid-cols-4 gap-3">
                        {COLOR_KEYS.map((colorKey) => {
                          const colorData = APPOINTMENT_COLORS[colorKey];
                          const isSelected = currentColorKey === colorKey;
                          return (
                            <button
                              key={colorKey}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setNewItem({ ...newItem, color: colorKey });
                                setShowColorPicker(false);
                              }}
                              className={`w-8 h-8 rounded-full transition-all ${colorData.bg} ${
                                isSelected ? 'ring-2 ring-offset-2 ring-slate-800 scale-110' : 'active:scale-90'
                              }`}
                              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Von</label>
                  <input
                    type="date"
                    value={newItem.from}
                    onChange={(e) => handleDateChange('from', e.target.value)}
                    className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Bis</label>
                  <input
                    type="date"
                    value={newItem.to}
                    min={newItem.from}
                    onChange={(e) => handleDateChange('to', e.target.value)}
                    className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Priorität: {newItem.priority}</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newItem.priority}
                  onChange={(e) => setNewItem({ ...newItem, priority: parseInt(e.target.value) })}
                  className="w-full mt-1 accent-emerald-600"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>1 (niedrig)</span>
                  <span>10 (hoch)</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Notiz (optional)</label>
                <textarea
                  value={newItem.note}
                  onChange={(e) => setNewItem({ ...newItem, note: e.target.value })}
                  placeholder="Zusätzliche Infos..."
                  className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Kontakt-Verknüpfung */}
              <div ref={contactSearchRef} className="relative">
                <label className="text-sm font-medium text-slate-700">Kontakt (optional)</label>
                {newItem.contactRef ? (
                  <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <User className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-emerald-700 font-medium flex-1">{newItem.contactRef.name}</span>
                    <button type="button" onClick={() => { setNewItem({...newItem, contactRef: null}); setContactSearch(''); }} className="text-emerald-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <div className="relative mt-1">
                      <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                      <input type="text" value={contactSearch} onChange={e => { setContactSearch(e.target.value); setContactSearchOpen(true); }}
                        onFocus={() => setContactSearchOpen(true)} placeholder="Kontakt suchen..."
                        className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    </div>
                    {contactSearchOpen && contactResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-50 bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                        {contactResults.map(c => (
                          <button key={c._id || c.id} type="button" onMouseDown={e => e.preventDefault()}
                            onClick={() => { setNewItem({...newItem, contactRef: { id: c._id || c.id, typ: 'teampartner', name: c.name }}); setContactSearch(''); setContactSearchOpen(false); }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-slate-400" />{c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveItem} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingItem ? 'Speichern' : 'Hinzufügen'}
                </Button>
                {editingItem && (
                  <Button 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-100 hover:bg-red-200 text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <Button onClick={() => { setShowAddModal(false); setEditingItem(null); setShowColorPicker(false); setShowDeleteConfirm(false); setContactSearch(''); }} variant="outline">
                  Abbrechen
                </Button>
              </div>
              {editingItem?.id && editingItem.contactRef && (
                <button
                  type="button"
                  onClick={() => handleToggleYearDone(editingItem)}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all mt-1 ${
                    editingItem.done
                      ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  }`}
                >
                  <Check className="w-4 h-4" />
                  {editingItem.done ? 'Als offen markieren' : 'Erledigt'}
                </button>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Transfer Selection Modal */}
      {showTransferModal && transferMonthIndex !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                Einträge übertragen - {MONTHS_DE[transferMonthIndex]}
              </h3>
              <button onClick={() => setShowTransferModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-slate-600 mb-3">
              Wähle die Einträge aus, die zur Monatsansicht übertragen werden sollen:
            </p>
            
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {getMonthItems(transferMonthIndex).filter(item => !item.transferred).length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">Keine Einträge zum Übertragen vorhanden</p>
              ) : (
                getMonthItems(transferMonthIndex).filter(item => !item.transferred).map(item => (
                  <div 
                    key={item.id}
                    onClick={() => toggleTransferSelection(item.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedForTransfer.includes(item.id) 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        selectedForTransfer.includes(item.id) 
                          ? 'bg-emerald-600 border-emerald-600 text-white' 
                          : 'border-slate-300'
                      }`}>
                        {selectedForTransfer.includes(item.id) && <Check className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700">{item.title || item.note}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {new Date(item.from).toLocaleDateString('de-DE')}
                          {item.to !== item.from && ` - ${new Date(item.to).toLocaleDateString('de-DE')}`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex gap-2 pt-2 border-t border-slate-200">
              <Button 
                onClick={executeTransfer} 
                disabled={selectedForTransfer.length === 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              >
                {selectedForTransfer.length > 0 
                  ? `${selectedForTransfer.length} Eintrag${selectedForTransfer.length > 1 ? 'e' : ''} übertragen` 
                  : 'Auswählen'}
              </Button>
              <Button onClick={() => setShowTransferModal(false)} variant="outline">
                Abbrechen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
